import { useCallback, useEffect, useRef, useState } from 'react';

import { SearchOutlined } from '@mui/icons-material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Button, IconButton, InputAdornment, Stack } from '@mui/material';

import { CissTextField } from 'components/fields';
import { ContentModal } from 'components/modal';
import { SubTitle } from 'components/text';
import { ListagemProvider, useListagemContext } from 'context/ListagemContext';
import { RequestOptionsType } from 'hooks/useRequestListagem';
import { ConfigPedidoLojasModalGrid } from 'pages/compra/configuracao-pedido/components';
import { LojasProps } from 'pages/compra/configuracao-pedido/types';

interface ConfigPedidoLojasModalProps {
    open: boolean;
    setOpen: (action: boolean) => void;
    handleAdicionarLojas: (lojas: LojasProps[]) => void;
    requestOptionsLojas?: RequestOptionsType;
}

function ConfigPedidoLojasModalContent({ open, setOpen, handleAdicionarLojas, requestOptionsLojas }: ConfigPedidoLojasModalProps) {
    const { setPage } = useListagemContext();
    const [valueFilter, setValueFilter] = useState('');
    const [selectedLojas, setSelectedLojas] = useState<LojasProps[]>([]);
    const lojaMapRef = useRef<Map<number, LojasProps>>(new Map());
    const inputRef = useRef<HTMLInputElement>(null);

    /**
     * Atualiza o valor do filtro de lojas e redefine a página para 1.
     */
    const handleSetValueFilter = (value: string) => {
        setValueFilter(value);

        setPage(1);
    };

    /**
     * Reseta os valores do modal.
     */
    const resetValues = useCallback(() => {
        if (!open) {
            setSelectedLojas([]);

            setValueFilter('');

            setPage(1);

            lojaMapRef.current.clear();
        }
    }, [open, setPage]);

    useEffect(resetValues, [resetValues]);

    return (
        <ContentModal
            closeButton
            open={open}
            setOpen={setOpen}
            sx={{
                padding: 2,
                borderRadius: 1,
                width: {
                    xs: '90vw',
                    md: 800,
                },
            }}
        >
            <>
                <SubTitle label="Incluir Lojas" />

                <CissTextField
                    inputRef={inputRef}
                    placeholder="Pesquisar por código ou nome"
                    label="Pesquisar"
                    size="small"
                    sx={{ flex: 1, mb: 2 }}
                    deboundTime={500}
                    value={valueFilter}
                    onChange={(e) => handleSetValueFilter(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();

                            if (inputRef.current) {
                                handleSetValueFilter(inputRef.current.value);
                            }
                        }
                    }}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        onClick={() => {
                                            if (inputRef.current) {
                                                handleSetValueFilter(inputRef.current.value);
                                            }
                                        }}
                                    >
                                        <SearchOutlined />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                <ConfigPedidoLojasModalGrid
                    open={open}
                    valueFilter={valueFilter}
                    requestOptionsLojas={requestOptionsLojas}
                    lojaMapRef={lojaMapRef}
                    selectedLojas={selectedLojas}
                    setSelectedLojas={setSelectedLojas}
                />

                <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="outlined" onClick={() => setOpen(false)}>
                        Cancelar
                    </Button>

                    <Button
                        variant="contained"
                        disabled={!selectedLojas.length}
                        onClick={() => {
                            handleAdicionarLojas(selectedLojas);

                            setOpen(false);
                        }}
                    >
                        Incluir
                    </Button>
                </Stack>
            </>
        </ContentModal>
    );
}

export function ConfigPedidoLojasModal(props: ConfigPedidoLojasModalProps) {
    return (
        <ListagemProvider>
            <ConfigPedidoLojasModalContent {...props} />
        </ListagemProvider>
    );
}
